import { Button, Heading, Input, Select, Spinner, Textarea } from '@vwfs-bronson/bronson-react';
import { RefObject } from 'react';
import { Team } from '../../fes';
import {
  TeamFormState,
  TeamPanelMode,
  formatArray,
  formatMetadataValue,
  teamStatuses,
} from './teamForm';

export function TeamDetailPanel({
  canEdit = true,
  error,
  form,
  isLoading,
  mode,
  onCancel,
  onEdit,
  onFormChange,
  onSave,
  sectionRef,
  team,
}: {
  canEdit?: boolean;
  error?: string;
  form: TeamFormState;
  isLoading: boolean;
  mode: TeamPanelMode;
  onCancel: () => void;
  onEdit: () => void;
  onFormChange: (field: keyof TeamFormState, value: string) => void;
  onSave: () => void;
  sectionRef?: RefObject<HTMLElement>;
  team: Team | null;
}) {
  if (!team && !isLoading) {
    return null;
  }

  const isEditing = mode === 'edit' || mode === 'saving';
  const isSaving = mode === 'saving';
  const canSave = form.name.trim().length > 0 && !isSaving;
  const editableFieldClassName = isEditing
    ? 'app-form-field app-form-field--editable'
    : 'app-form-field app-form-field--readonly';
  const editableWideFieldClassName = `${editableFieldClassName} app-form-field--wide`;

  return (
    <section ref={sectionRef} className="app-team-panel app-team-detail-panel" aria-labelledby="team-detail-heading">
      <div className="app-team-panel-header">
        <div>
          <div className="app-kicker">Team Detail</div>
          <Heading level={2} id="team-detail-heading" className="app-panel-title">
            {team?.name || 'Loading team'}
          </Heading>
        </div>
        <div className="app-actions">
          {!isEditing ? (
            canEdit ? (
              <Button onClick={onEdit} disabled={!team || isLoading}>
                Edit
              </Button>
            ) : null
          ) : (
            <>
              <Button onClick={onSave} disabled={!canSave}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button secondary onClick={onCancel} disabled={isSaving}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="app-detail-loading" role="status">
          <Spinner center>Loading team...</Spinner>
        </div>
      ) : (
        <>
          {error ? (
            <div className="app-result app-team-error" role="alert">
              {error}
            </div>
          ) : null}
          <div className="app-detail-grid">
            <label className="app-form-field app-form-field--readonly" htmlFor="team-id">
              <span>Team ID</span>
              <Input id="team-id" value={team?.teamId || '-'} readOnly />
            </label>
            <label className={editableFieldClassName} htmlFor="team-name">
              <span>Name</span>
              <Input
                id="team-name"
                value={form.name}
                onChange={event => onFormChange('name', event.target.value)}
                readOnly={!isEditing}
              />
            </label>
            <label className={editableWideFieldClassName} htmlFor="team-description">
              <span>Description</span>
              <Textarea
                id="team-description"
                value={form.description}
                onChange={event => onFormChange('description', event.target.value)}
                readOnly={!isEditing}
                rows={3}
              />
            </label>
            <label className={editableFieldClassName} htmlFor="team-status">
              <span>Status</span>
              <Select
                id="team-status"
                value={form.status}
                onChange={event => onFormChange('status', event.target.value)}
                disabled={!isEditing}
              >
                {teamStatuses.map(status => (
                  <Select.Item key={status} value={status}>
                    {status}
                  </Select.Item>
                ))}
              </Select>
            </label>
            <label className={editableFieldClassName} htmlFor="team-installation-id">
              <span>Installation ID</span>
              <Input
                id="team-installation-id"
                value={!isEditing && !form.installationId ? '-' : form.installationId}
                onChange={event => onFormChange('installationId', event.target.value)}
                readOnly={!isEditing}
              />
            </label>
            <label className="app-form-field app-form-field--readonly app-form-field--wide" htmlFor="team-allowed-repos">
              <span>Allowed Repos</span>
              <Textarea id="team-allowed-repos" value={formatArray(team?.allowedRepos)} readOnly rows={3} />
            </label>
            <label className={editableFieldClassName} htmlFor="team-jira-mode">
              <span>Jira Connection Mode</span>
              <Select
                id="team-jira-mode"
                value={form.jiraConnectionMode}
                onChange={event => onFormChange('jiraConnectionMode', event.target.value)}
                disabled={!isEditing}
              >
                <Select.Item value="default">default (shared technical user)</Select.Item>
                <Select.Item value="dedicated">dedicated team connection</Select.Item>
                <Select.Item value="disabled">disabled</Select.Item>
              </Select>
            </label>
            <label className={editableWideFieldClassName} htmlFor="team-jira-base-url">
              <span>Jira Base URL</span>
              <Input
                id="team-jira-base-url"
                value={!isEditing && !form.jiraBaseUrl ? '-' : form.jiraBaseUrl}
                onChange={event => onFormChange('jiraBaseUrl', event.target.value)}
                readOnly={!isEditing || form.jiraConnectionMode !== 'dedicated'}
                placeholder="https://jira.example.com"
              />
              {isEditing ? (
                <span className="app-field-hint">
                  Only dedicated mode needs a URL. Its credential stays in Vault.
                </span>
              ) : null}
            </label>
            <label className="app-form-field app-form-field--readonly" htmlFor="team-created-at">
              <span>Created At</span>
              <Input id="team-created-at" value={formatMetadataValue(team?.metadata?.createdAt)} readOnly />
            </label>
            <label className="app-form-field app-form-field--readonly" htmlFor="team-updated-at">
              <span>Updated At</span>
              <Input id="team-updated-at" value={formatMetadataValue(team?.metadata?.updatedAt)} readOnly />
            </label>
          </div>

        </>
      )}
    </section>
  );
}
